import { Link } from 'react-router-dom'
import { ExternalLink, History, Play } from 'lucide-react'
import clsx from 'clsx'
import { Card, CardContent, CardHeader } from '@/components/common/Card'
import { formatDateTime } from '@/utils/format'
import { hasStageNotices, isLocalRunUrl, safeExternalUrl } from '@/utils/patchStage'
import { ApprovalsList } from './ApprovalsList'
import { AssessmentTable } from './AssessmentTable'
import { DryRunPanel } from './DryRunPanel'
import { StageStatusBadge } from './PatchBadges'
import { PlanPanel } from './PlanPanel'
import { ResultsTable } from './ResultsTable'
import { RolloutPanel } from './RolloutPanel'
import { RunProgress } from './RunProgress'
import { PostPatchPanel } from './PostPatchPanel'
import { PrePatchPanel } from './PrePatchPanel'
import { StageActions } from './StageActions'
import { StageNotices } from './StageNotices'
import type { PatchCampaign, PatchMe, PatchStage } from '@/types/patching'
import styles from './Patching.module.css'

interface EnvStagePanelProps {
  campaign: PatchCampaign
  stage: PatchStage
  me: PatchMe | undefined
  meLoading: boolean
  meError: string | null
}

/** Everything about one environment: status, targets, plan, assessment, rollout, results, approvals, actions. */
export function EnvStagePanel({ campaign, stage, me, meLoading, meError }: EnvStagePanelProps) {
  const env = stage.environment.toUpperCase()
  const localRun = isLocalRunUrl(stage.run_url)
  const runUrl = localRun ? null : safeExternalUrl(stage.run_url)
  const hasBanner = hasStageNotices(stage)
  return (
    <div className={styles.panelLayout} data-testid="env-panel" data-env={stage.environment}>
      <Card noPadding>
        <CardHeader
          title={
            <span className={styles.chips}>
              {env} <StageStatusBadge status={stage.status} />
              {stage.dry_run && <span className={clsx(styles.badge, styles.badgePending)}>dry run</span>}
            </span>
          }
          subtitle={stage.updated_at ? `Updated ${formatDateTime(stage.updated_at)}` : undefined}
          actions={
            runUrl ? (
              <a href={runUrl} target="_blank" rel="noopener noreferrer" className={styles.chip} data-testid="run-link">
                <ExternalLink size={12} /> Open pipeline
              </a>
            ) : localRun ? (
              <Link to="?tab=report" className={styles.chip} data-testid="run-link">
                <ExternalLink size={12} /> Run report
              </Link>
            ) : undefined
          }
        />
        {hasBanner && (
          <div className={clsx(styles.section, styles.stack)}>
            <StageNotices stage={stage} />
          </div>
        )}

        <RunProgress stage={stage} />

        <section className={styles.section} aria-label="Targets">
          <h3 className={styles.sectionTitle}>
            <Play size={14} /> Targets
          </h3>
          {stage.targets && stage.targets.vm_names.length > 0 ? (
            <>
              <p className={styles.muted} style={{ margin: '0 0 8px' }}>
                {stage.targets.resource_groups && stage.targets.resource_groups.length > 1 ? 'Resource groups' : 'Resource group'}{' '}
                <span className={styles.mono}>{stage.targets.resource_groups && stage.targets.resource_groups.length > 1 ? stage.targets.resource_groups.join(', ') : stage.targets.resource_group}</span>
              </p>
              {stage.targets.source && (
                <p className={styles.muted} style={{ margin: '0 0 8px' }} data-testid="targets-source">
                  From tag <span className={styles.mono}>{stage.targets.source.tag_name}</span>, copied {formatDateTime(stage.targets.source.snapshot_at)}. Later
                  changes to the tag do not affect this campaign.
                </p>
              )}
              <span className={styles.chips}>
                {stage.targets.vm_names.map((vm) => {
                  const group = stage.targets?.vms?.find((item) => item.name.toLowerCase() === vm.toLowerCase())?.resource_group
                  return (
                    <span key={vm} className={clsx(styles.chip, styles.chipMono)} title={group ? `Resource group ${group}` : undefined}>
                      {vm}
                      {group && <span className={styles.muted}> · {group}</span>}
                    </span>
                  )
                })}
              </span>
            </>
          ) : (
            <p className={styles.muted}>No targets recorded for this environment.</p>
          )}
          {stage.skipped_targets && stage.skipped_targets.length > 0 && (
            <div className={clsx(styles.alert, styles.alertWarning)} role="status" data-testid="skipped-targets" style={{ marginTop: 10 }}>
              <span>
                Left out of the last execute because they no longer qualified:
                <ul className={styles.notesList}>
                  {stage.skipped_targets.map((skipped) => (
                    <li key={skipped.resource_id || skipped.vm_name}>
                      <span className={styles.mono}>{skipped.vm_name}</span>: {skipped.reason}
                    </li>
                  ))}
                </ul>
              </span>
            </div>
          )}
        </section>

        <PlanPanel plan={stage.plan} />
        <AssessmentTable stage={stage} />
        <RolloutPanel rollout={stage.rollout} />
        <DryRunPanel result={stage.dry_run_result} status={stage.status} />
        <ResultsTable results={stage.results} acceptance={stage.acceptance} />
      </Card>

      <div className={styles.sideColumn}>
        <Card>
          <CardHeader title="Actions" subtitle="The portal enforces every rule; its messages are shown as-is." />
          <CardContent>
            <StageActions campaign={campaign} stage={stage} me={me} meLoading={meLoading} meError={meError} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader title="Pre-patch validation" subtitle="Mandatory failures block scheduling." />
          <CardContent>
            <PrePatchPanel campaignId={campaign.id} env={stage.environment} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader title="Post-patch validation" subtitle="Runs after Azure finishes the install." />
          <CardContent>
            <PostPatchPanel campaignId={campaign.id} env={stage.environment} checks={stage.post_validation ?? []} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={
              <span className={styles.chips}>
                <History size={14} /> Approvals
              </span>
            }
          />
          <CardContent>
            <ApprovalsList approvals={stage.approvals} />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
